import React, { useEffect, useState, useCallback } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { isSignedInWithGoogle } from "../utils/auth";
import { useAuth } from "../contexts/AuthContext";
import "../styles/google-drive-folder-picker.css";

type DriveItem = {
    id: string;
    name: string;
    mimeType: string;
    size?: number;
    isFolder: boolean;
    modifiedTime?: string;
};

type FolderContentsResponse = {
    folderId: string;
    folderName: string;
    items: DriveItem[];
};

interface GoogleDriveFolderPickerProps {
    onFolderSelected?: (response: FolderContentsResponse) => void;
    onError?: (error: string) => void;
    disabled?: boolean;
}

declare global {
    interface Window {
        gapi: any;
        google: any;
    }
}

const GoogleDriveFolderPicker: React.FC<GoogleDriveFolderPickerProps> = ({
    onFolderSelected,
    onError,
    disabled = false,
}) => {
    const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentFolder, setCurrentFolder] = useState<FolderContentsResponse | null>(null);
    const [showContents, setShowContents] = useState(false);
    const { googleConnected } = useAuth();
    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();

    useEffect(() => {
        // Load Google API script
        const loadGoogleApi = () => {
            if (window.gapi) {
                setPickerApiLoaded(true);
                return;
            }

            const script = document.createElement("script");
            script.src = "https://apis.google.com/js/api.js";
            script.async = true;
            script.defer = true;
            script.onload = () => {
                window.gapi.load("picker", () => {
                    setPickerApiLoaded(true);
                });
            };
            script.onerror = () => {
                if (onError) {
                    onError("Failed to load Google Picker API");
                }
            };
            document.body.appendChild(script);
        };

        loadGoogleApi();
    }, [onError]);

    const fetchFolderContents = useCallback(async (folderId: string, folderName: string) => {
        setIsLoading(true);
        try {
            // Call backend API to get folder contents
            const response = await fetch(buildApiUrl(`api/user/drive/folder/${folderId}/contents`), {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Authentication failed. Please sign in again.");
                } else if (response.status === 403) {
                    throw new Error("Permission denied. Please check folder access permissions.");
                } else {
                    throw new Error(`Failed to fetch folder contents: ${response.statusText}`);
                }
            }

            const rawData = await response.json();

            // Map backend response to FolderContentsResponse format
            const items: DriveItem[] = (rawData.files || []).map((file: any) => ({
                id: file.fileId || file.id,
                name: file.fileName || file.name,
                mimeType: file.mimeType,
                size: file.sizeBytes || file.size || 0,
                isFolder: file.mimeType === 'application/vnd.google-apps.folder',
                modifiedTime: file.createdTime || file.grantedAt || file.modifiedTime,
            }));

            const folderData: FolderContentsResponse = {
                folderId: rawData.folderId,
                folderName: rawData.folderName,
                items: items,
            };

            // Update current folder state
            setCurrentFolder(folderData);
            setShowContents(true);

            // Notify parent component
            if (onFolderSelected) {
                onFolderSelected(folderData);
            }
        } catch (error: any) {
            if (onError) {
                onError(error.message || "Failed to fetch folder contents");
            }
        } finally {
            setIsLoading(false);
        }
    }, [onFolderSelected, onError]);

    /**
     * Handle picker callback - extract folder ID and fetch its contents
     * The act of selecting via Picker grants the app access to that folder
     */
    const pickerCallback = useCallback(
        async (data: any) => {
            if (data.action === window.google.picker.Action.PICKED) {
                const selectedDoc = data.docs[0];

                // Verify that a folder was selected
                if (selectedDoc.mimeType === "application/vnd.google-apps.folder") {
                    const folderId = selectedDoc.id;
                    const folderName = selectedDoc.name;

                    // Fetch folder contents from backend
                    await fetchFolderContents(folderId, folderName);
                } else {
                    if (onError) {
                        onError("Please select a folder, not a file.");
                    }
                }
            } else if (data.action === window.google.picker.Action.CANCEL) {
                // User canceled the picker
            }
        },
        [onError, fetchFolderContents]
    );

    const createPicker = useCallback(async () => {
        if (!pickerApiLoaded || !window.google || !window.google.picker) {
            if (onError) {
                onError("Google Picker API not loaded");
            }
            return;
        }

        if (!signedInWithGoogle) {
            if (onError) {
                onError("Please sign in with Google to access Drive folders");
            }
            return;
        }

        setIsLoading(true);

        try {
            // Get OAuth access token from backend
            const tokenResponse = await fetch(buildApiUrl("api/google-drive/access-token"), {
                credentials: "include",
            });

            if (!tokenResponse.ok) {
                if (tokenResponse.status === 401) {
                    throw new Error("Authentication failed. Please sign in with Google again.");
                }
                throw new Error("Failed to get access token");
            }

            const { accessToken } = await tokenResponse.json();

            if (!accessToken) {
                throw new Error("No access token available. Please sign in with Google.");
            }

            /**
             * Option 1: Folder + Files View
             * Configure Google Picker to allow selecting a folder AND viewing its files.
             * This gives explicit permission to both the folder and its contents.
             * Users can navigate into folders to see and grant access to files.
             */
            const docsView = new window.google.picker.DocsView()
                .setIncludeFolders(true)           // Show folders in the view
                .setSelectFolderEnabled(true)      // Allow selecting folders
                .setMode(window.google.picker.DocsViewMode.LIST)  // List view shows files clearly
                .setParent('root');                // Start at root (or specific folder)

            const picker = new window.google.picker.PickerBuilder()
                .addView(docsView)
                .setOAuthToken(accessToken)
                .setCallback(pickerCallback)
                .setTitle("Select a folder (navigate into it to grant file access)")
                .enableFeature(window.google.picker.Feature.NAV_HIDDEN)  // Cleaner UI
                .build();

            picker.setVisible(true);
        } catch (error: any) {
            if (onError) {
                onError(error.message || "Failed to open Google Drive Picker");
            }
        } finally {
            setIsLoading(false);
        }
    }, [pickerApiLoaded, signedInWithGoogle, onError, pickerCallback]);

    const handleItemClick = (item: DriveItem) => {
        if (item.isFolder) {
            // Navigate into subfolder
            fetchFolderContents(item.id, item.name);
        }
        // For files, you could add download/preview logic here
    };

    const handleButtonClick = () => {
        if (!signedInWithGoogle) {
            if (onError) {
                onError("Please sign in with Google to access Drive folders");
            }
            return;
        }
        createPicker();
    };

    return (
        <div className="google-drive-folder-picker">
            <button
                onClick={handleButtonClick}
                disabled={disabled || isLoading || !pickerApiLoaded || !signedInWithGoogle}
                className="drive-folder-picker-button"
                data-testid="drive-folder-picker-button"
                aria-label="Select folder from Google Drive"
            >
                {isLoading ? "Loading..." : "Select Folder from Google Drive"}
            </button>

            {/* Display folder contents after selection */}
            {showContents && currentFolder && (
                <div className="folder-contents-container">
                    <div className="folder-header">
                        <h3>{currentFolder.folderName}</h3>
                        <span className="item-count">
                            {currentFolder.items.length} item{currentFolder.items.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {isLoading ? (
                        <div className="loading-spinner">
                            <span>Loading folder contents...</span>
                        </div>
                    ) : currentFolder.items.length === 0 ? (
                        <div className="empty-folder">
                            <p>This folder is empty</p>
                        </div>
                    ) : (
                        <div className="items-list">
                            {currentFolder.items.map((item) => (
                                <div
                                    key={item.id}
                                    className={`item-row ${item.isFolder ? 'folder' : 'file'}`}
                                    onClick={() => handleItemClick(item)}
                                    role={item.isFolder ? "button" : "listitem"}
                                    tabIndex={item.isFolder ? 0 : -1}
                                >
                                    <div className="item-icon">
                                        {item.isFolder ? '📁' : getFileIcon(item.mimeType)}
                                    </div>
                                    <div className="item-details">
                                        <div className="item-name">{item.name}</div>
                                        <div className="item-meta">
                                            {!item.isFolder && item.size && (
                                                <span className="item-size">{formatFileSize(item.size)}</span>
                                            )}
                                            {item.modifiedTime && (
                                                <span className="item-date">
                                                    {new Date(item.modifiedTime).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {item.isFolder && <span className="chevron">›</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Helper function to get file icon based on mime type
const getFileIcon = (mimeType: string): string => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('video')) return '🎥';
    if (mimeType.includes('audio')) return '🎵';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('document') || mimeType.includes('word')) return '📝';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📽️';
    return '📎';
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default GoogleDriveFolderPicker;

